import axios, { AxiosError } from 'axios';
import MainURL from '../../MainURL';
import { attachTokenRefresh } from '../../authInterceptor';
import type { RuralCalling } from './pastorOptions';

// 담임목사 청빙 중개 API (server/routers/pastor/PastorCall.js)
// 요청자는 서버가 로그인 토큰으로 판정한다 — userAccount 를 보내지 않는다.

const api = axios.create({ baseURL: `${MainURL}/pastorcall` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('refreshToken');
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});
attachTokenRefresh(api);

export function errorMessage(error: unknown, fallback = '처리하지 못했습니다.') {
  const err = error as AxiosError<{ error?: string }>;
  return err.response?.data?.error || fallback;
}

export interface PastorProfile {
  id?: number;
  realName: string;
  phone: string;
  email: string;
  birthYear: number | null;
  ageBand?: string;
  denomination: string;
  ordinationYear: number | null;
  ministryYears: number | null;
  currentRole: string;
  currentChurch: string;
  currentRegion: string;
  currentChurchSize: string;
  excludeChurches: string[];
  hopeRegionTypes: string[];
  hopeSidos: string[];
  ruralCalling: RuralCalling | '';
  churchSizePrefs: string[];
  needs: string[];
  needsNote: string;
  availableFrom: string;
  philosophyKeywords: string[];
  philosophy: string;
  strengthKeywords: string[];
  strengths: string;
  sermonUrls: string[];
  education: string;
  career: string;
  familyNote: string;
  isActive: boolean;
  updatedAt?: string | null;
  files?: PastorFile[];
}

/** 구직 서류 (자기소개서 등) — 이력서와 함께 동의한 교회에만 전달 */
export interface PastorFile {
  id: number;
  kind: string;
  name: string;
  size: number;
  createdAt: string;
}
export const FILE_KINDS = ['자기소개서', '추천서', '증명서', '기타'];
export const FILE_RULES = { maxFiles: 10, maxFileMb: 10, extensions: ['.pdf', '.hwp', '.hwpx', '.doc', '.docx', '.jpg', '.jpeg', '.png'] };

export const fileSizeText = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);

/** 인증이 필요한 파일 응답을 브라우저 다운로드로 */
export function saveBlob(data: Blob, name: string) {
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 교회가 전달 링크로 내려받는 서류 주소 (로그인 없이) */
export const deliveredFileUrl = (token: string, fileId: number) => `${MainURL}/pastorcall/delivered/${token}/files/${fileId}`;

export type VerifyChannel = 'phone' | 'email';

/** 청빙 의뢰서 */
export interface CallRequest {
  id?: number;
  churchName: string;
  denomination: string;
  sido: string;
  sigungu: string;
  regionType: string;
  address: string;
  attendance: string;
  foundedYear: number | null;
  intro: string;
  offerConditions: string[];
  salaryNote: string;
  desiredNote: string;
  managerName: string;
  managerRole: string;
  managerPhone: string;
  managerEmail: string;
  allowPromote: boolean;
  status?: RequestStatus;
  createdAt?: string;
  recruitPath?: string;
}

export type RequestStatus = 'RECEIVED' | 'MATCHING' | 'DONE' | 'CLOSED';
export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  RECEIVED: '접수',
  MATCHING: '후보 찾는 중',
  DONE: '청빙 완료',
  CLOSED: '종료',
};

export type DeliveryStatus = 'CONSENT_PENDING' | 'INTERESTED' | 'DELIVERED' | 'DECLINED' | 'EXPIRED' | 'CANCELED';
export const DELIVERY_STATUS_LABEL: Record<DeliveryStatus, string> = {
  CONSENT_PENDING: '응답 대기',
  INTERESTED: '전달 검토 중',
  DELIVERED: '이력서 전달됨',
  DECLINED: '거절',
  EXPIRED: '기한 만료',
  CANCELED: '취소',
};

export interface InboxDelivery {
  id: number;
  origin: 'ADMIN' | 'PASTOR';
  status: DeliveryStatus;
  adminMessage: string;
  pastorNote: string;
  createdAt: string;
  expiresAt: string | null;
  deliveredAt: string | null;
  churchViewed: boolean;
  church: {
    churchName: string;
    denomination: string;
    region: string;
    regionType: string;
    attendance: string;
    intro: string;
    offerConditions: string[];
    salaryNote: string;
    desiredNote: string;
    recruitPath: string;
  };
}

export interface InboxPromotion {
  id: number;
  sourceType: 'RECRUIT' | 'REQUEST';
  sourceId: number;
  title: string;
  church: string;
  region: string;
  link: string;
  message: string;
  sentAt: string;
  interested: boolean;
}

export interface JobRecruit {
  kind: string;
  id: number;
  path: string;
  title: string;
  church: string;
  region: string;
  pay: string;
  deadline: string;
  postedAt: string;
}

export interface JobRequest extends CallRequest {
  id: number;
  myStatus: DeliveryStatus | null;
}

export interface MyRequest extends CallRequest {
  id: number;
  status: RequestStatus;
  candidates: { name: string; summary: string; deliveredAt: string; viewPath: string }[];
}

export const pastorApi = {
  stats: () => api.get<{ stats: { pastors: number; ruralOpen: number; requests: number; delivered: number } }>('/stats').then((r) => r.data.stats),
  jobs: () => api.get<{ recruits: JobRecruit[]; requests: JobRequest[] }>('/jobs').then((r) => r.data),
  consult: (body: { churchName: string; contactName: string; phone: string; region?: string; memo?: string }) => api.post('/consult', body),
  createRequest: (body: CallRequest) => api.post<{ requestId: number }>('/request', body).then((r) => r.data.requestId),
  delivered: (token: string) => api.get<{ churchName: string; deliveredAt: string; profile: PastorProfile }>(`/delivered/${token}`).then((r) => r.data),
  me: () => api.get<{ userType: string; pastorProfile: PastorProfile | null }>('/me').then((r) => r.data),

  saveProfile: (body: PastorProfile) => api.post<{ profile: PastorProfile }>('/pastor/profile', body).then((r) => r.data),
  deleteProfile: () => api.delete('/pastor/profile'),
  sendVerify: (channel: VerifyChannel, target: string, name?: string) => api.post<{ ttlSec: number }>('/pastor/verify/send', { channel, target, name }).then((r) => r.data),
  checkVerify: (channel: VerifyChannel, target: string, code: string) => api.post('/pastor/verify/check', { channel, target, code }),
  setActive: (active: boolean) => api.post('/pastor/active', { active }),
  uploadFile: (file: File, kind: string) => {
    const body = new FormData();
    body.append('kind', kind);
    body.append('file', file);
    return api.post<{ files: PastorFile[] }>('/pastor/files', body).then((r) => r.data.files);
  },
  downloadFile: (f: PastorFile) => api.get(`/pastor/files/${f.id}`, { responseType: 'blob' }).then((r) => saveBlob(r.data, f.name)),
  deleteFile: (id: number) => api.delete(`/pastor/files/${id}`),
  inbox: () => api.get<{ deliveries: InboxDelivery[]; promotions: InboxPromotion[] }>('/pastor/inbox').then((r) => r.data),
  respond: (id: number, accept: boolean, note?: string) => api.post(`/pastor/deliveries/${id}/${accept ? 'accept' : 'decline'}`, { note }),
  interest: (requestId: number, note?: string) => api.post(`/pastor/requests/${requestId}/interest`, { note }),

  myRequests: () => api.get<{ items: MyRequest[] }>('/church/requests').then((r) => r.data.items),
  updateRequest: (id: number, body: CallRequest) => api.put(`/church/requests/${id}`, body),
  finishRequest: (id: number, status: 'DONE' | 'CLOSED') => api.post(`/church/requests/${id}/finish`, { status }),
  deleteRequest: (id: number) => api.delete(`/church/requests/${id}`),
};

/** 유튜브 주소 → 썸네일 */
export function youtubeThumb(url: string) {
  const m = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/|\/live\/)([\w-]{11})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : '';
}
